import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import type { Reservation, StatusItem } from '../types';

interface CarType {
  name: string;
  code: string;
  [key: string]: any;
}

interface VehicleOption {
  id: string | number;
  [key: string]: any;
}

interface UseReservationFiltersOptions {
  reservations: Reservation[];
  vehicleIdToCode: Record<string, string>;
  reservationStatusList: StatusItem[];
  carTypesList: CarType[];
}

export function useReservationFilters({ reservations, vehicleIdToCode, reservationStatusList, carTypesList }: UseReservationFiltersOptions) {
  const [filterCarType, setFilterCarType] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterVehicle, setFilterVehicle] = useState<VehicleOption[]>([]);
  const [filterNoVehicle, setFilterNoVehicle] = useState<boolean>(false);
  const [filterInvoice, setFilterInvoice] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [displayDateStart, setDisplayDateStart] = useState<string>('');
  const [displayDateEnd, setDisplayDateEnd] = useState<string>('');
  const [dateFilterTarget, setDateFilterTarget] = useState<string>('rental'); // 'rental' or 'booking'
  const [dateRangeAnchor, setDateRangeAnchor] = useState<HTMLElement | null>(null);
  const [page, setPage] = useState<number>(1);

  const filteredReservations = useMemo(() => {
    let result = reservations;

    // 車種フィルタ
    if (filterCarType.length > 0) {
      const targetCodes = filterCarType.map(name => {
        const type = carTypesList.find(c => c.name === name);
        return type ? type.code.toLowerCase() : null;
      }).filter(Boolean) as string[];
      const targetNames = filterCarType.map(n => n.toLowerCase());

      result = result.filter(res => {
        const rType = (res.car_type ?? res.re_car_type ?? res.req_car_type ?? '').toLowerCase();
        return targetNames.includes(rType) || targetCodes.includes(rType);
      });
    }

    // ステータスフィルタ
    if (filterStatus.length > 0) {
      result = result.filter(res => {
        const s = res.status != null ? String(res.status).toLowerCase() : '';
        if (!s) return false;

        const opt = reservationStatusList.find(o =>
          String(o.id).toLowerCase() === s ||
          (o.code && String(o.code).toLowerCase() === s)
        );

        const code = opt ? String(opt.code || opt.id).toLowerCase() : s;
        return filterStatus.some(fs => String(fs).toLowerCase() === code);
      });
    }

    // 車両フィルタ (Autocomplete)
    if (filterVehicle.length > 0) {
      const targetIds = filterVehicle.map(v => String(v.id));
      result = result.filter(res => {
        const vid = res.vehicle_id ?? res.vehicleId;
        return vid != null && targetIds.includes(String(vid));
      });
    }

    // 車両未割当フィルタ
    if (filterNoVehicle) {
      result = result.filter((res) => {
        const vehicleId = res.vehicle_id ?? res.vehicleId;
        const vehicleCode = vehicleId != null ? vehicleIdToCode[String(vehicleId)] : null;
        return !vehicleCode || vehicleCode.trim() === '';
      });
    }

    // 請求書未発行フィルタ
    if (filterInvoice) {
      result = result.filter((res) => !(res as any).invoice_link);
    }

    // 検索フィルタ
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter((res) => {
        const vehicleId = res.vehicle_id ?? res.vehicleId;
        const vehicleCode = vehicleId != null ? vehicleIdToCode[String(vehicleId)] : '';
        const searchFields = [
          res.id,
          res.contact_code,
          res.customer_name,
          res.customer_email,
          res.detail?.contact_plan,
          res.status,
          res.car_type,
          res.re_car_type,
          res.req_car_type,
          vehicleCode,
        ];
        return searchFields.some((field) =>
          field != null && String(field).toLowerCase().includes(query)
        );
      });
    }

    // 表示期間フィルタ (ローカル) - 貸出日（開始日）または予約日（booking_date）ベース
    if (displayDateStart && displayDateEnd) {
      const rangeStart = dayjs(displayDateStart).startOf('day');
      const rangeEnd = dayjs(displayDateEnd).endOf('day');
      result = result.filter(res => {
        const targetDateValue = dateFilterTarget === 'booking' ? res.booking_date : res.start_date;
        if (!targetDateValue) return false;

        const compareDate = dayjs(targetDateValue);
        return (compareDate.isAfter(rangeStart) || compareDate.isSame(rangeStart)) &&
          (compareDate.isBefore(rangeEnd) || compareDate.isSame(rangeEnd));
      });
    }

    return result;
  }, [reservations, searchQuery, filterNoVehicle, filterInvoice, vehicleIdToCode, filterCarType, filterVehicle, filterStatus, displayDateStart, displayDateEnd, dateFilterTarget, reservationStatusList, carTypesList]);

  const handleResetFilters = useCallback(() => {
    setFilterStatus([]);
    setFilterCarType([]);
    setFilterVehicle([]);
    setFilterNoVehicle(false);
    setFilterInvoice(false);
    setSearchQuery('');
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setPage(1);
  }, []);

  const handleFilterNoVehicleToggle = useCallback(() => {
    setFilterNoVehicle(prev => !prev);
    setPage(1);
  }, []);

  const handleFilterInvoiceToggle = useCallback(() => {
    setFilterInvoice(prev => !prev);
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_event: any, value: number) => {
    setPage(value);
  }, []);

  return {
    filterCarType, setFilterCarType,
    filterStatus, setFilterStatus,
    filterVehicle, setFilterVehicle,
    filterNoVehicle,
    filterInvoice,
    searchQuery,
    displayDateStart, setDisplayDateStart,
    displayDateEnd, setDisplayDateEnd,
    dateFilterTarget, setDateFilterTarget,
    dateRangeAnchor, setDateRangeAnchor,
    page, setPage,
    filteredReservations,
    handleResetFilters,
    handleSearchChange,
    handleClearSearch,
    handleFilterNoVehicleToggle,
    handleFilterInvoiceToggle,
    handlePageChange,
  };
}
